import React, { type FC, forwardRef } from 'react';
import {
  Box,
  Icon,
  Spinner,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { SelectActionMenu } from '@web-config-app/core-react-ui';
import type { SelectMenuItemGroup } from '@web-config-app/core-react-ui';
import { useConfigApp } from '@web-config-app/core-react';

export type AccessibleTenant = {
  tenantId: string;
  tenantName: string;
};

export type TenantSwitcherProps = {
  currentTenantId: string;
  icon: string;
  accessibleTenants: AccessibleTenant[];
  onSelectTenant: (tenantId: string) => void;
  isDiscoveryLoading: boolean;
  discoveryError: Error | null;
  environmentLabel?: string;
};

/** Non-interactive error banner rendered inside the dropdown footer. */
const DiscoveryErrorBanner: FC<{ error: Error }> = ({ error }) => (
  <StackLayout
    orientation="horizontal"
    verticalAlignment="center"
    spacing="$half"
    css={{
      padding: '$half $one',
      borderTop: '1px solid $onSurfaceBorderSubdued',
    }}
    data-testid="tenant-switcher-discovery-error"
  >
    <Icon
      icon="statusInformationCircle"
      size="1rem"
      tint="$criticalIcon"
      css={{ flexShrink: 0 }}
    />
    <UtilityText css={{ color: '$criticalText', typography: '$caption' }}>
      {error.message}
    </UtilityText>
  </StackLayout>
);

/** Dropdown trigger button showing the icon, name, and chevron. Uses plain text (not a Link) to avoid nesting an interactive `<a>` inside the `<button>`. Uses `forwardRef` so Radix DropdownMenu can attach its ref. Ref type is `HTMLDivElement` because GDS `StackLayout` always renders a div regardless of the `as` prop. */
const TenantSwitcherTrigger = forwardRef<
  HTMLDivElement,
  {
    icon: string;
    currentTenantName: string;
    environmentLabel?: string;
  }
>((props, ref) => {
  const { icon, currentTenantName, environmentLabel, ...radixProps } = props;
  return (
    <StackLayout
      as="button"
      ref={ref}
      orientation="horizontal"
      verticalAlignment="center"
      spacing="$threeQuarters"
      aria-label={`Switch tenant. Current tenant: ${currentTenantName}`}
      data-testid="tenant-switcher-trigger"
      css={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '0',
        borderRadius: '$inputField',
        '&:hover': {
          backgroundColor: '$inputBackgroundHovered',
        },
        '&:active': {
          backgroundColor: '$inputBackgroundPressed',
        },
      }}
      {...radixProps}
    >
      <Icon icon={icon} />
      <UtilityText
        css={{ typography: '$buttonOne', color: '$interactiveActionSubdued' }}
      >
        {environmentLabel
          ? `${currentTenantName} / ${environmentLabel}`
          : currentTenantName}
      </UtilityText>
      <Icon
        icon="interfaceChevronDown"
        size="1.25rem"
        tint="$interactiveActionSubdued"
      />
    </StackLayout>
  );
});

/**
 * Displays the current tenant in the app header with an optional
 * dropdown to switch between accessible tenants.
 *
 * Rendering priority:
 * 1. Discovery loading → spinner
 * 2. Tenants → dropdown menu via SelectActionMenu
 */
export const TenantSwitcher: FC<TenantSwitcherProps> = ({
  currentTenantId,
  icon,
  accessibleTenants,
  onSelectTenant,
  isDiscoveryLoading,
  discoveryError,
  environmentLabel,
}) => {
  const { tenantName: currentTenantNameFromConfig } = useConfigApp();
  const currentTenant = accessibleTenants.find(
    (t) => t.tenantId === currentTenantId,
  );
  const currentTenantName =
    currentTenant?.tenantName ?? currentTenantNameFromConfig;

  if (isDiscoveryLoading) {
    return (
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing="$threeQuarters"
      >
        <Icon icon={icon} />
        <Spinner loading size="small" />
      </StackLayout>
    );
  }

  const menuGroups: SelectMenuItemGroup[] = [
    {
      menuItems: accessibleTenants.map((tenant) => ({
        id: tenant.tenantId,
        label: tenant.tenantName,
        selected: tenant.tenantId === currentTenantId,
        action: () => {
          if (tenant.tenantId !== currentTenantId) {
            onSelectTenant(tenant.tenantId);
          }
        },
      })),
    },
  ];

  return (
    <Box css={{ position: 'relative' }}>
      <SelectActionMenu
        menuGroups={menuGroups}
        contentCss={{ width: '14.5rem' }}
        footerContent={
          discoveryError && <DiscoveryErrorBanner error={discoveryError} />
        }
      >
        <TenantSwitcherTrigger
          icon={icon}
          currentTenantName={currentTenantName}
          environmentLabel={environmentLabel}
        />
      </SelectActionMenu>
    </Box>
  );
};
